import React, {
  createContext,
  ReactElement,
  useContext,
  useState,
} from 'react';

import { TChildrenOnly } from 'types/common';
import { isAndroid } from 'utils/platform';
import { TFlagTypes } from 'types/flags';
import { configManager } from 'utils/configuration';
import { useNativeEventListener } from 'hooks/useNativeEventListener';
import { setCookie } from 'utils/storage';
import {
  functionExistsForNativeAndroidInterface,
  functionExistsForWebkit,
  runNativeFunction,
} from 'utils/native';
import { createAllABTestCookiesForUser } from 'utils/abTestCookieHelper';
import { TUserFlagContext } from './UserFlagProvider.types';
import useUserFlagRequests from './UserFlagProvider.hooks';
import { USER_FLAG_PROVIDER_DATA } from './UserFlagProvider.constants';

export const UserFlagContext = createContext<TUserFlagContext | undefined>(
  undefined
);

const USER_FLAGS_HANDLER_KEY = 'setUserInteractionData';

export function UserFlagProvider({ children }: TChildrenOnly): ReactElement {
  const { getUserFlags, updateUserFlag } = useUserFlagRequests();
  const [userFlags, setUserFlags] = useState<TFlagTypes | Partial<TFlagTypes>>(
    {}
  );

  const mobileFlagHandlerAvailable =
    functionExistsForWebkit(USER_FLAGS_HANDLER_KEY) ||
    functionExistsForNativeAndroidInterface(USER_FLAGS_HANDLER_KEY);

  const config = configManager.getInstance().get;
  const abTestingConfigCookieValues = config('AB_TESTING_VARIABLES') as [];

  useNativeEventListener({
    listen: document,
    name: USER_FLAGS_HANDLER_KEY,
    callback: (event: CustomEvent<{ userInteractionData: TFlagTypes }>) => {
      const userData = event.detail.userInteractionData;
      setCookie('userData', JSON.stringify(event.detail.userInteractionData));
      const userCookieValues = setAllUserCookies(userData);
      setUserFlags({
        ...USER_FLAG_PROVIDER_DATA,
        ...userData,
        abTestingValues: userCookieValues,
      });
    },
  });

  const keys = Object.keys(USER_FLAG_PROVIDER_DATA);

  async function fetchAll() {
    if (mobileFlagHandlerAvailable) {
      return;
    }
    const data = await getUserFlags(keys);
    if (data && Object.keys(data).length > 0) {
      setUserFlags({ ...USER_FLAG_PROVIDER_DATA, ...data, ...userFlags });
    } else {
      setUserFlags({ ...USER_FLAG_PROVIDER_DATA, ...userFlags });
    }
  }

  function saveUserFlagsToDevice(data: Partial<TFlagTypes>) {
    if (isAndroid())
      runNativeFunction(USER_FLAGS_HANDLER_KEY, JSON.stringify(data));
    else runNativeFunction(JSON.stringify(data), {}, USER_FLAGS_HANDLER_KEY);
  }

  async function set(
    key: string,
    value: string | number | boolean,
    addition?: Partial<TFlagTypes>
  ) {
    const newData = { ...userFlags, ...addition, [key]: value };
    setUserFlags(newData);

    if (mobileFlagHandlerAvailable) {
      saveUserFlagsToDevice(newData);
    } else {
      await updateUserFlag(key, value);
    }
  }

  function setAllUserCookies(data: TFlagTypes) {
    const userCookieValues = createAllABTestCookiesForUser(
      data.abTestingValues,
      abTestingConfigCookieValues
    );
    setCookie('abTestingValues', userCookieValues);
    return userCookieValues;
  }

  return (
    <UserFlagContext.Provider
      value={{
        userFlags,
        saveUserFlag: set,
        getUserFlags: fetchAll,
        mobileFlagHandlerAvailable,
      }}>
      {children}
    </UserFlagContext.Provider>
  );
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function useUserFlag(): TUserFlagContext {
  const context = useContext(UserFlagContext);
  if (context === undefined) {
    throw new Error('useUserFlags must be used within a UserFlagsProvider');
  }
  return context;
}
